/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { Box, Divider, Grid, List, ListItemButton, SxProps, Theme, Tooltip, Typography, ListItemIcon , Drawer} from '@h9/ui-lib';
import { Link, useLocation } from "react-router-dom";
import { HARDWARE_INVENTORY, IT_DASHBOARD, ONBOARDING, PROFILES, routes, SETTINGS, RouteVisibilityCtx, SIMULATORS, VMZ } from "../constants/routes";
import { softwareVersion } from "../store/context/dashboardSlice";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { Images } from "~/components/GraphicalResources";
import { useMemo } from "react";
import { settingsAirgapEnabled, settingsSystemDashboardConsoleEnabled, settingsSystemOnboardingEnabled } from "~/store/context/settingsSlice";
import { AppSubject, CrudAction, useAppAbility } from "~/rbac";
import { useMobile } from "~/hooks/useMobile";
import { selectMobileSideDrawerOpen, utilityActions } from "~/store/context/utilitySlice";
import { useCurrentRole } from "~/hooks/useCurrentRole";

export const DRAWER_WIDTH = 60;
export const DRAWER_WIDTH_MOBILE = 280;


type Props = {};

const getDrawerStyles = (isMobile: boolean) => ({
  zIndex: 1202,
  width: isMobile ? DRAWER_WIDTH_MOBILE : DRAWER_WIDTH,
  flexShrink: 0,
  textAlign: "center",
  "& .MuiDrawer-paper": {
    background: "#226A58",
    width: isMobile ? DRAWER_WIDTH_MOBILE : DRAWER_WIDTH,
    position: "relative",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: 0
  },
});

const getListItemStyles = (isMobile: boolean) => ({
  padding: isMobile ? "12px 8px" : "8px",
  width: isMobile ? "auto" : "36px",
  height: isMobile ? "auto" : "36px",
  marginX: "auto",
  marginBottom: "4px",
  borderRadius: "8px",
  minHeight: isMobile ? "48px" : "36px",
  "& .MuiListItemIcon-root": {
    minWidth: 0,
    marginRight: isMobile ? "12px" : 0,
  },
  "& .MuiSvgIcon-root": {
    width: "20px",
    height: "20px",
    fill: "#FFFFFF"
  },
  "&:hover": {
    backgroundColor: "#FFFFFF26 !important",
  },
  "&.Mui-selected": {
    backgroundColor: "#FFFFFF33",
  },
  "& img": {
    width: "20px",
    height: "20px"
  }
});

const styles = {
  logo: {
    padding: "22px 0 24px"
  },
  listContainer: {
    padding: "8px 12px",
    "& a": {
      color: "#0000008a",
    },
  },
  divider: {
    borderColor: "#EFEFEF26",
    marginTop: "8px",
    marginBottom: "12px",
  },
  versionContainer: {
    flexDirection: "column",
    alignItems: "center",
    marginY: "16px"
  },
  versionText: {
    fontSize: "10px",
    fontWeight: 500,
    color: "white",
    opacity: 0.7,
    lineHeight: 1.35
  },
  tooltip: {
    bgcolor: "#292B2F",
    marginLeft: "8px !important",
    padding: "8px",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 600,
    color: "#F7F7F7",
  },
  mobileText: {
    color: "#FFFFFF",
    fontSize: "12px",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "180px"
  }
} satisfies Record<string, SxProps<Theme>>;

const SideDrawerNew = (props: Props) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const version = useAppSelector(softwareVersion);
  const ability = useAppAbility();
  const onboardingEnabled = useAppSelector(settingsSystemOnboardingEnabled);
  const monitorPageEnabled = useAppSelector(settingsSystemDashboardConsoleEnabled);
  const airgapEnabled = useAppSelector(settingsAirgapEnabled);
  const isMobile = useMobile();
  const mobileSideDrawerOpen = useAppSelector(selectMobileSideDrawerOpen);
  function canHighlightRouteIcon(pathname: string, routePath: string) {
    if (!routePath || !pathname) return false;

    if (routePath === "/") {
        return pathname === "/";
    }

    const normalizedPathname = pathname.endsWith("/") ? pathname : `${pathname}/`;
    const normalizedRoutePath = routePath.endsWith("/") ? routePath : `${routePath}/`;

    return normalizedPathname.includes(normalizedRoutePath);
  }

  const { isKpiOnly: isKpiOnlyUser, isOperator, isRadioInstaller: isRadioInstallerUser } = useCurrentRole();

  const showOnboarding = useMemo(() => {
    if (isKpiOnlyUser) return false;
    return onboardingEnabled && (
      // At least one of the view permissions should exist for the Getting Started page to be visible
      ability.can(CrudAction.view, AppSubject.radioSite) ||
      ability.can(CrudAction.view, AppSubject.radioGroup) ||
      ability.can(CrudAction.view, AppSubject.dnn) ||
      ability.can(CrudAction.view, AppSubject.deviceGroup) ||
      ability.can(CrudAction.view, AppSubject.zone) ||
      ability.can(CrudAction.view, AppSubject.edge) ||
      ability.can(CrudAction.view, AppSubject.device)
    )
  }, [isKpiOnlyUser, onboardingEnabled, ability]);

  const filteredRoutes = useMemo(() => {
    let prevRouteName = "";
    const filteredRoutes = routes.filter((route) => {

      // Filter out duplicate routes (espcially the duplicate separator elements)
      const currentRouteName = route.name || "";
      if(currentRouteName === prevRouteName) return false;

      if(route.path?.includes(ONBOARDING) && !showOnboarding) return false;

      if(route.path?.includes(IT_DASHBOARD) && (!monitorPageEnabled || isKpiOnlyUser)) return false;

      // MCS-3868: SETTINGS (Administration) hidden too — the RAN_KPI_ONLY permission set can't view
      // any of its data (networks/accessConfig/caCert all 403), so the page only shows stuck spinners.
      if (isKpiOnlyUser && [VMZ, SIMULATORS, HARDWARE_INVENTORY, SETTINGS].includes(route.path ?? "")) return false;
      if (isKpiOnlyUser && route.path === PROFILES) return false;
      if (isRadioInstallerUser && route.path === SETTINGS) return false;

      // Custom visibility predicate (e.g. Users entry: airgap+user.VIEW OR operator)
      const visibleFn = (route as { visible?: (ctx: RouteVisibilityCtx) => boolean }).visible;
      if (typeof visibleFn === "function") {
        if (!visibleFn({ ability, isOperator, airgapEnabled })) return false;
        prevRouteName = currentRouteName;
        return true;
      }

      // Include the routes that don't need filtering based on object type
      if (!route.objectType || route.name === "|") {
        prevRouteName = currentRouteName;
        return true;
      }

      // Filter out the routes if any object type is not in the permissions array with VIEW permission
      if (route.objectType?.every(objectType => ability.can(CrudAction.view, objectType as any))) {
        prevRouteName = currentRouteName;
        return true;
      }

      return false;
    })

    // If the first element is a separator, remove it
    if(filteredRoutes[0]?.name === "|") filteredRoutes.shift();

    // If the last element is a separator, remove it
    if(filteredRoutes[filteredRoutes.length - 1]?.name === "|") filteredRoutes.pop();

    return filteredRoutes

  }, [ability, showOnboarding, monitorPageEnabled, airgapEnabled, isOperator, isKpiOnlyUser, isRadioInstallerUser]);

  const { versionNumber, buildNumber } = useMemo(() => {
    if (version) {
      const [major, minor, buildNumber] = version.split(".");
      return { versionNumber: `${major ? major : ""}.${minor ? minor : ""}`, buildNumber };
    }
    return { versionNumber: "", buildNumber: "" };
  }, [version])

  return (
    <>
    <Drawer variant={isMobile ? "temporary" : "permanent"} open={mobileSideDrawerOpen} onClose={() => dispatch(utilityActions.setMobileSideDrawerOpen(false))} sx={getDrawerStyles(isMobile)}>
      <Grid height="100%" container flexDirection="column" justifyContent="space-between">
        {/* The following code is picked from the existing Side Drawer component */}
        <List sx={styles.listContainer}>
          <Grid sx={styles.logo}>
            <img src={Images.highway9Logo} alt="" />
          </Grid>
          {filteredRoutes.map((route, index) => {
            if (route.name === "|") return <Divider key={index} sx={styles.divider} />;
            return (
              <Tooltip
                title={!isMobile ? route.name : ""}
                aria-label={route.name}
                placement="right"
                key={route.name}
                slotProps={
                  {
                    tooltip: {
                      sx: styles.tooltip
                    }
                  }
                }
              >
                <Link style={{ textDecoration: "none" }} to={route.path === "/" ? "/" : route.path ?? "#"}>
                  <ListItemButton sx={getListItemStyles(isMobile)} selected={canHighlightRouteIcon(location.pathname, route.path ?? "")}>
                    <ListItemIcon>
                      {route.icon}
                    </ListItemIcon>
                    {isMobile && (
                      <Typography sx={styles.mobileText}>
                        {route.name}
                      </Typography>
                    )}
                  </ListItemButton>
                </Link>
              </Tooltip>
            );
          })}
        </List>
      </Grid>
      {version &&
        <Box>
          <Divider sx={styles.divider} />
          <Grid container sx={styles.versionContainer}>
            <Typography variant="caption" sx={styles.versionText}>
              {versionNumber}
            </Typography>
            <Typography variant="caption" sx={styles.versionText}>
              {buildNumber}
            </Typography>
          </Grid>
        </Box>
      }
    </Drawer>
    </>
  );
};

export default SideDrawerNew;
